import { useCart } from '../../contexts/CartContext';
import { Link } from 'react-router-dom';
import { FaShoppingCart, FaPlus, FaMinus } from 'react-icons/fa';
import { COMMERCE_SOURCES } from '../../services/analyticsService';
import useProductActions from '../../hooks/useProductActions';
import ProductImage from './ProductImage';
import { rutaProducto } from '../../utils/productUrl';

export default function ProductCard({ product, source = COMMERCE_SOURCES.CATALOGO }) {
  const { getItemQuantity, updateQuantity } = useCart();
  const { addToCartTracked, buyNow } = useProductActions(source);
  const enOferta = product.Oferta?.toLowerCase() === 'si';
  const enPreventa = product.Preventa?.toLowerCase() === 'si';
  const esNuevo = product.EsNuevo?.toLowerCase() === 'si';
  const stock = Number(product.cantidad ?? product.stock ?? 0);
  const agotado = stock <= 0;

  // Manejo seguro de precios (pueden venir null/undefined o como string)
  const toNumber = v => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const precioPublico = toNumber(
    product.Precio_Publico ?? product.precio_publico ?? product.precio
  );
  const precioOferta = toNumber(product.Precio_Oferta ?? product.precio_oferta);
  const quantityInCart = getItemQuantity(product);

  const handleAddToCart = () => {
    if (agotado) return;
    addToCartTracked(product, 1);
  };

  const handleIncreaseQuantity = () => {
    if (quantityInCart >= stock) return;
    updateQuantity(product, quantityInCart + 1);
  };

  const handleDecreaseQuantity = () => {
    if (quantityInCart > 1) {
      updateQuantity(product, quantityInCart - 1);
    } else {
      updateQuantity(product, 0); // This will remove the item
    }
  };

  return (
    <div className={`bg-df-surface border border-df-border rounded-xl shadow-lg p-4 flex flex-col justify-between h-full relative overflow-hidden transition-colors hover:border-df-accent/40${agotado ? ' opacity-75' : ''}`}>
      {/* Badge de agotado */}
      {agotado && (
        <div className="absolute top-2 left-2 bg-df-surface2 text-df-muted text-xs font-bold px-3 py-1 rounded shadow z-10">
          AGOTADO
        </div>
      )}

      {/* Badge de oferta */}
      {enOferta && !agotado && (
        <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded shadow z-10">
          ¡Oferta!
        </div>
      )}

      {/* Listón diagonal de preventa (esquina superior derecha) */}
      {enPreventa && (
        <div className="absolute -right-7 top-4 w-[120px] bg-green-600 text-white text-xs font-bold text-center py-1 rotate-[45deg] shadow-md z-10">
          PREVENTA
        </div>
      )}

      {/* Badge de nuevo */}
      {esNuevo && !agotado && (
        <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded shadow z-10">
          ✨ NUEVO
        </div>
      )}

      {/* Contenido superior — enlaza a la ficha del producto.
          Antes este bloque tenía cursor-pointer y solo disparaba un evento de
          analítica: parecía clicable pero no llevaba a ningún lado. La vista de
          producto se registra ahora en la ficha, no aquí, para no contar como
          visita un clic sobre una página que nunca se abrió. */}
      <Link
        to={rutaProducto(product)}
        state={{ source }}
        className="block group"
        aria-label={`Ver ficha de ${product.Juego}`}
      >
        <ProductImage
          src={product.Imagen}
          alt={product.Juego}
          agotado={agotado}
          className="aspect-square w-full mb-3 p-3 rounded-lg"
        />
        <p className="text-sm text-df-muted">{product.marca}</p>
        <h3 className="text-lg font-semibold leading-snug line-clamp-2 text-df-text group-hover:text-df-accent transition-colors">
          {product.Juego}
        </h3>
      </Link>

      {/* Precio + botón */}
      <div className="mt-4">
        {enOferta && precioOferta !== null && precioPublico !== null ? (
          <div className="mb-2">
            <p className="text-sm text-df-muted line-through">${precioPublico.toLocaleString()}</p>
            <p className="text-df-gold font-bold text-lg">${precioOferta.toLocaleString()}</p>
          </div>
        ) : precioPublico !== null ? (
          <p className="text-df-gold font-bold mb-2">${precioPublico.toLocaleString()}</p>
        ) : (
          <p className="text-df-muted font-medium mb-2">Consultar</p>
        )}

        {agotado ? (
          <div className="flex items-center justify-center">
            <button
              disabled
              className="bg-df-surface2 text-df-muted text-sm font-semibold px-4 py-2.5 rounded flex items-center justify-center gap-2 flex-1 cursor-not-allowed"
            >
              AGOTADO
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {quantityInCart === 0 ? (
              <button
                onClick={handleAddToCart}
                className="bg-df-accent-strong hover:bg-df-accent-deep text-white text-sm font-semibold px-4 py-2.5 rounded flex items-center justify-center gap-2 transition-colors duration-200 flex-1 whitespace-nowrap"
              >
                <FaShoppingCart className="text-sm" />
                AGREGAR
              </button>
            ) : (
              <div className="flex items-center gap-1 flex-1">
                <button
                  onClick={handleDecreaseQuantity}
                  className="bg-df-surface2 hover:bg-df-border text-df-text w-9 h-9 rounded flex items-center justify-center transition-colors"
                >
                  <FaMinus className="text-xs" />
                </button>
                <span className="bg-df-accent/20 text-df-accent px-3 py-2.5 rounded font-semibold text-center flex-1 text-sm">
                  {quantityInCart}
                </span>
                <button
                  onClick={handleIncreaseQuantity}
                  disabled={quantityInCart >= stock}
                  className={`${quantityInCart >= stock ? 'bg-df-surface2 cursor-not-allowed text-df-muted' : 'bg-df-accent-strong hover:bg-df-accent-deep text-white'} w-9 h-9 rounded flex items-center justify-center transition-colors`}
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
            )}

            <button
              onClick={() => buyNow(product, 1)}
              className="bg-df-gold hover:bg-yellow-400 text-df-bg text-sm font-semibold px-4 py-2.5 rounded transition-colors duration-200 flex-1 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <span>🛒</span>
              <span>COMPRAR</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
